import { Box, Stack, StatusChip, Tooltip } from '@presov.sk/presov-ui-react';
import React from 'react';

/** `gap: 0.5` v MUI spacingu. Meranie potrebuje hodnotu v px. */
const GAP_PX = 4;

type Item = { key: string; label: string };

type Props = {
  items: Item[];
  /** čo kresliť, keď nie je nič */
  empty?: React.ReactNode;
};

/**
 * Riadok neutrálnych štítkov, ktorý sa nikdy nezalomí: čo sa nezmestí, sa
 * zbalí do `+X` a tooltip nad ním vymenuje zvyšok. Počíta sa zo skutočnej
 * šírky, nie z pevného počtu - bunka je na širokom monitore trikrát širšia
 * než na notebooku.
 *
 * Meranie je prevzaté z [AdminReportCategories](./AdminReportCategories.tsx)
 * a z rovnakých dôvodov: merací riadok drží vždy všetky štítky a oba riadky
 * sú mimo toku (`position: absolute`), inak by si bunka tabuľky vynútila
 * plnú šírku štítkov a namiesto `+X` by prišiel vodorovný scrollbar.
 * Kategórie podnetov si nechávajú vlastný komponent - nesú bodku stavu,
 * tinty a tooltip so stavom nálezu, čo sem nepatrí.
 */
const OverflowChips: React.FC<Props> = ({ items, empty = null }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = React.useState(items.length);
  const [height, setHeight] = React.useState(24);

  const recalculate = React.useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const chips = Array.from(
      measure.querySelectorAll<HTMLElement>('[data-chip]'),
    );
    const available = container.clientWidth;
    // Nezmeraný layout (skrytá záložka) - ResizeObserver zavolá znova.
    if (!chips.length || !available) return;

    setHeight(chips[0].offsetHeight || 24);
    const overflow = measure.querySelector<HTMLElement>('[data-overflow]');
    const overflowWidth = overflow ? overflow.offsetWidth + GAP_PX : 0;

    let used = 0;
    let fits = 0;
    chips.every((chip, index) => {
      const width = chip.offsetWidth + (fits ? GAP_PX : 0);
      // Poslednému štítku netreba nechávať miesto na `+X`.
      const reserve = index === chips.length - 1 ? 0 : overflowWidth;
      if (used + width + reserve > available) return false;
      used += width;
      fits += 1;
      return true;
    });

    // Aspoň jeden štítok, skrátený trojbodkou, povie viac než samotné `+5`.
    setVisibleCount(Math.max(fits, 1));
  }, []);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    recalculate();
    const observer = new ResizeObserver(recalculate);
    observer.observe(container);
    if (measureRef.current) observer.observe(measureRef.current);
    return () => observer.disconnect();
  }, [items, recalculate]);

  if (!items.length) return <>{empty}</>;

  const visible = items.slice(0, visibleCount);
  const hidden = items.slice(visibleCount);

  const chip = (item: Item, measuring = false) => (
    <Box
      component='span'
      key={item.key}
      {...(measuring ? { 'data-chip': true } : {})}
      sx={{
        display: 'inline-flex',
        // `minWidth: 0` až mimo merania: v riadku sa štítok smie skrátiť,
        // merací ale potrebuje prirodzenú šírku.
        ...(measuring ? {} : { minWidth: 0, maxWidth: '100%' }),
        '& > *': { overflow: 'hidden', textOverflow: 'ellipsis' },
        whiteSpace: 'nowrap',
      }}
    >
      <StatusChip tone='neutral'>{item.label}</StatusChip>
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        height,
        overflow: 'hidden',
      }}
    >
      <Stack
        ref={measureRef}
        aria-hidden
        direction='row'
        gap={0.5}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 'max-content',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {items.map((item) => chip(item, true))}
        <Box component='span' data-overflow sx={{ display: 'inline-flex' }}>
          <StatusChip tone='neutral'>{`+${items.length}`}</StatusChip>
        </Box>
      </Stack>

      <Stack
        direction='row'
        gap={0.5}
        sx={{ position: 'absolute', top: 0, left: 0, maxWidth: '100%' }}
      >
        {visible.map((item) => (
          <Tooltip key={item.key} title={item.label}>
            {chip(item)}
          </Tooltip>
        ))}
        {hidden.length ? (
          <Tooltip
            title={
              <Stack sx={{ p: 0.5, gap: 0.25 }}>
                {hidden.map((item) => (
                  <span key={item.key}>{item.label}</span>
                ))}
              </Stack>
            }
          >
            <Box
              component='span'
              sx={{ display: 'inline-flex', flexShrink: 0, cursor: 'default' }}
            >
              <StatusChip tone='neutral'>{`+${hidden.length}`}</StatusChip>
            </Box>
          </Tooltip>
        ) : null}
      </Stack>
    </Box>
  );
};

export default OverflowChips;
